// contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, companiesAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/* ------------------------------------------------------------------
   Storage helpers.

   "Remember me" chooses WHICH storage holds the session:
     remember === true   -> localStorage   (survives closing the browser)
     remember === false  -> sessionStorage (dies with the tab)

   The old code wrote to sessionStorage when remember was false but then
   only ever READ from localStorage on boot — and bailed out early
   whenever rememberMe wasn't 'true'. Net effect: unchecked logins were
   logged out on every refresh, and the checkbox appeared to do nothing.
   Every read below now checks both stores.
------------------------------------------------------------------- */
const REMEMBER_KEY = 'rememberMe';
const EMAIL_KEY = 'rememberedEmail';
const TOKEN_KEY = 'authToken';
const USER_KEY = 'userData';

const readStored = (key) =>
  localStorage.getItem(key) ?? sessionStorage.getItem(key);

const clearStoredCredentials = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => readStored(TOKEN_KEY));
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem(REMEMBER_KEY) === 'true'
  );
  // Prefilled into the login form when "Remember me" was used last time.
  const [rememberedEmail, setRememberedEmail] = useState(
    () => localStorage.getItem(EMAIL_KEY) || ''
  );

  const fetchCompanyDetails = async (companyId) => {
    try {
      const response = await companiesAPI.getById(companyId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching company details:', error);
      return null;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      // Read from whichever store holds this session.
      const storedToken = readStored(TOKEN_KEY);
      const storedUser = readStored(USER_KEY);

      if (!storedToken || !storedUser) {
        clearStoredCredentials();
        setLoading(false);
        return;
      }

      try {
        let userData = JSON.parse(storedUser);

        if (userData.companyId && !userData.companyName) {
          const companyDetails = await fetchCompanyDetails(userData.companyId);
          if (companyDetails) {
            userData = {
              ...userData,
              companyName: companyDetails.name,
              company: companyDetails
            };
            // Write the enriched copy back to the SAME store it came from.
            const store =
              localStorage.getItem(TOKEN_KEY) ? localStorage : sessionStorage;
            store.setItem(USER_KEY, JSON.stringify(userData));
          }
        }

        setUser(userData);
        setToken(storedToken);
      } catch (error) {
        console.error('Stored session is unreadable, clearing it:', error);
        clearStoredCredentials(); // was `this.clearStoredCredentials()` — threw
      }

      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email, password, remember = false) => {
    try {
      const response = await authAPI.login(email, password);
      const responseData = response.data;

      let authToken, userData;

      if (responseData.token) {
        authToken = responseData.token;
        userData = responseData.user || responseData;
      } else if (responseData.accessToken) {
        authToken = responseData.accessToken;
        userData = responseData.user || responseData;
      } else if (responseData.jwtToken) {
        authToken = responseData.jwtToken;
        userData = responseData.user || responseData;
      } else {
        authToken = responseData;
        userData = { email };
      }

      if (!authToken) {
        throw new Error('No authentication token received from server');
      }

      if (userData.companyId && !userData.companyName) {
        const companyDetails = await fetchCompanyDetails(userData.companyId);
        if (companyDetails) {
          userData = {
            ...userData,
            companyName: companyDetails.name,
            company: companyDetails
          };
        }
      }

      // Persist the preference only after the credentials actually worked,
      // so a failed attempt can't leave the flag in a half-applied state.
      setRememberMe(remember);
      localStorage.setItem(REMEMBER_KEY, remember.toString());

      // Start from a clean slate so a token can never linger in the store
      // we are NOT using this session.
      clearStoredCredentials();

      const store = remember ? localStorage : sessionStorage;
      store.setItem(TOKEN_KEY, authToken);
      store.setItem(USER_KEY, JSON.stringify(userData));

      if (remember) {
        localStorage.setItem(EMAIL_KEY, email);
        setRememberedEmail(email);
      } else {
        localStorage.removeItem(EMAIL_KEY);
        setRememberedEmail('');
      }

      setToken(authToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      console.error('❌ Login error:', error);

      let errorMessage = 'Login failed';

      if (error.code === 'ECONNABORTED') {
        errorMessage =
          'Connection timeout. Please check your internet connection and try again.';
      } else if (error.response) {
        errorMessage =
          error.response.data?.message ||
          error.response.data?.title ||
          `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage =
          'Cannot connect to server. Please check if the backend is running.';
      } else {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    clearStoredCredentials();
    // The remembered email and the preference itself deliberately survive a
    // logout — that is the whole point of "Remember me" on a shared device
    // you own. Only the credentials go.
    setToken(null);
    setUser(null);
  };

  // Applies a partial update (e.g. from a Profile save) to BOTH the live
  // `user` state and whichever store actually holds this session.
  //
  // Profile.jsx used to write straight to storage itself, picking the
  // store by re-reading the `rememberMe` flag from localStorage. That's
  // one indirect hop removed from the truth -- the store that actually
  // holds the *current* token/userData pair -- and it never touched React
  // state at all, so a saved name change didn't show up in the sidebar
  // (Layout's getCompanyName()/user.firstName) until the next full
  // reload. Keying off `token` here (same as `updateRememberMe` below)
  // finds the store the live session is really using, and setUser makes
  // the change visible immediately everywhere `useAuth()` is read.
  const updateUser = (patch) => {
    setUser((prev) => {
      const merged = { ...prev, ...patch };

      const hasStoredToken =
        typeof window !== 'undefined' && !!localStorage.getItem(TOKEN_KEY);
      const store = hasStoredToken ? localStorage : sessionStorage;
      store.setItem(USER_KEY, JSON.stringify(merged));

      return merged;
    });
  };

  // Lets the user flip the preference while already signed in: moves the
  // live session between stores instead of forcing a re-login.
  const updateRememberMe = (remember) => {
    setRememberMe(remember);
    localStorage.setItem(REMEMBER_KEY, remember.toString());

    if (!token || !user) return;

    clearStoredCredentials();
    const store = remember ? localStorage : sessionStorage;
    store.setItem(TOKEN_KEY, token);
    store.setItem(USER_KEY, JSON.stringify(user));

    if (remember && user.email) {
      localStorage.setItem(EMAIL_KEY, user.email);
      setRememberedEmail(user.email);
    } else if (!remember) {
      localStorage.removeItem(EMAIL_KEY);
      setRememberedEmail('');
    }
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    rememberMe,
    rememberedEmail,
    updateRememberMe,
    updateUser,
    isAuthenticated: !!token,
    isSuperAdmin: user?.userType === 0,
    isCompanyAdmin: user?.userType === 1,
    isEmployee: user?.userType === 2
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
